"use client";
import { useEffect, useState } from "react";

import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import Logo from "../../assets/logo.png";
import { AlignJustify } from "lucide-react";
import CommonLayout from "./CommonLayout";
import CitySelectbox from "../city/CitySelectbox";
import { getCityFromUrl, generateCityUrl } from "@/lib/cityUtils";
import { ensureFromToParams } from "@/lib/formatHour";
import { useDateRange } from "../date/DateRangeContext";
import HomePageRedirectBtn from "@/components/ui/HomePageRedirectBtn";
import DynamicNavItems from "@/components/navigation/DynamicNavItems";
import ModalForMobileNav from "../ui/ModalForMobileNav";
import LoginFlowModal from "@/components/login/LoginFlowModal";
import { storage } from "@/lib/storage";
import { validateAndClearInvalidToken } from "@/lib/auth"
import PhoneQueries from "../ui/PhoneQueries";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ModalAnalytics } from "@/lib/analyticsName";
import { track } from "@/lib/tracking";

type SelectionRange = { startDate: Date; endDate: Date; key: string };
export default function Header() {
  const [cityPath, setCityPath] = useState("/bike-rentals");
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { range } = useDateRange();
  const { createClickHandler } = useAnalytics();

  // Create mobile menu toggle handler with analytics
  const createMobileMenuHandler = (action: 'open' | 'close') => {
    return createClickHandler(
      () => setOpenMobileMenu(action === 'open'),
      action === 'open' ? ModalAnalytics.Open : ModalAnalytics.Close,
      ModalAnalytics.Category,
      {
        modal_name: 'mobile_navigation',
        action,
        current_page: pathname,
        city_path: cityPath
      }
    );
  };

  const handleLoginModalOpen = () => {
    track('login_clicked', { source_page: document.title });
    setIsLoginModalOpen(true);
  };

  // Handle login modal close
  const handleLoginModalClose = (open: boolean) => {
    setIsLoginModalOpen(open);
    
    // If login modal is closing, also ensure mobile menu is closed
    if (!open) {
      setOpenMobileMenu(false);
    }
  };

  // Mark as hydrated after first render
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Auto-detect if we're on the home page, city page, or search page
  const isHomeCityPage =
    pathname === "/" ||
    (pathname.split("/").length === 2 && pathname !== "/" && !pathname.startsWith("/blogs") && !pathname.startsWith("/about") && !pathname.startsWith("/contact") && !pathname.startsWith("/faq") && !pathname.startsWith("/privacy") && !pathname.startsWith("/terms") && !pathname.startsWith("/store") && !pathname.startsWith("/thank-you")) || // /[city] but exclude other static pages
    (pathname.split("/").length === 3 && pathname.includes("/bike-rentals")) || // /[city]/bike-rentals
    pathname.startsWith("/search");

  useEffect(() => {
    if (!isHydrated) return; // Don't run until hydrated

    const updateCity = () => {
      const cityFromUrl = getCityFromUrl(pathname);
      if (cityFromUrl.name) {
        // Use searchParams instead of window.location.search
        const paramsWithDates = ensureFromToParams(
          [{ ...range, key: "selection" } as SelectionRange],
          searchParams
        );
        setCityPath(
          generateCityUrl(cityFromUrl.name, "bike-rentals", paramsWithDates)
        );
      } else {
        setCityPath("/");
      }
    };
    updateCity();
  }, [pathname, range, searchParams, isHydrated]);

  // Check authentication status when on profile pages
  useEffect(() => {
    if (!isHydrated) return;

    const checkAuthOnProfilePage = async () => {
      if (pathname.includes('/userProfile')) {
        const loginDetails = storage.getLoginDetails();
        if (loginDetails.token) {
          const isValidToken = await validateAndClearInvalidToken();
          if (!isValidToken) {
            storage.clearLoginDetails();
            window.dispatchEvent(new Event('authChange'));
          }
        }
      }
    };

    checkAuthOnProfilePage();
  }, [pathname, isHydrated]);

  return (
    <header className="bg-white  w-full fixed top-0 z-30">
      <div className="md:hidden mt-4">
      <PhoneQueries/>
      </div>
      <CommonLayout>
        {/* Mobile header for home page */}
        {/* Mobile header: logo left, hamburger right - same everywhere */}
        <div className="md:hidden flex items-center justify-between w-full py-2">
          <HomePageRedirectBtn className="btn-primary">
            <Image
              src={Logo}
              alt="ridengoLogo"
              className="w-[80px] h-[21px] md:w-[120px] md:h-[32px]"
            />
          </HomePageRedirectBtn>
          {isHomeCityPage ? (
            <CitySelectbox
              width="200px"
              height="36px"
              backgroundColor="#fff"
              boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.2)"
              textSize="12px"
              mobileColumns={1}
              desktopColumns={2}
            />
          ) : null}
          <div
            className="cursor-pointer flex-shrink-0"
            onClick={createMobileMenuHandler(openMobileMenu ? 'close' : 'open')}
          >
            <AlignJustify />
          </div>
        </div>

        <div className="hidden md:flex justify-between items-center py-2 ">
          <div className="flex items-center justify-between w-full md:w-fit ">
            <div className="flex items-center">
              <HomePageRedirectBtn className="btn-primary">
                <Image
                  src={Logo}
                  alt="ridengoLogo"
                  className="w-[80px] h-[21px] md:w-[120px] md:h-[32px]"
                />
              </HomePageRedirectBtn>
            </div>
            <div className="flex">
              <div className="w-[1px] h-8 bg-gray-300 mx-2 md:mx-4" />
              <PhoneQueries/>
            </div>
          </div>

          {/* Desktop nav */}
          <nav>
            <DynamicNavItems 
              cityPath={cityPath} 
              onLoginClick={handleLoginModalOpen}
            />
          </nav>
        </div>
      </CommonLayout>

      {/* Mobile nav */}
      {openMobileMenu && (
        <ModalForMobileNav setCloseModal={() => setOpenMobileMenu(false)}>
          <nav className="peer-checked:block bg-white p-4">
            <DynamicNavItems
              cityPath={cityPath}
              isMobile={true}
              onItemClick={() => setOpenMobileMenu(false)}
              onLoginClick={() => {
                setOpenMobileMenu(false);
                handleLoginModalOpen();
              }}
              className={`${
                cityPath === "" ? "!text-[#F05A24]" : "text-gray-500"
              }`}
            />
          </nav>
        </ModalForMobileNav>
      )}

      {/* Login/Profile Completion Modal - Outside mobile nav modal */}
      {isLoginModalOpen && (
        <LoginFlowModal
          setModalOpen={handleLoginModalClose}
          setLoginProfileBtnText={() => {}}
          setUserData={() => {}}
          isBooking={false}
          forceDetailsPage={false}
        />
      )}
    </header>
  );
}
